import { roundTimestamp } from '../../../helpers/formatCurrentDate';
import { reportDayjs } from '../../../helpers/reportRange';
import { haversineDistance } from '../../../helpers/map-util';
import { AssetProperties } from '../../asset/types/asset.type';
import { DeviceProperties, EDeviceType } from '../../device/types/device.type';
import { OperationModeDetailProperties } from '../../operationMode/types/operationMode.type';
import {
  EReportDataInterval,
  ILogAE,
  ILogFlowmeter,
  ILogGPS,
  ILogOperationMode,
  ILogReport,
  ILogRpm,
  IReportSummary,
} from '../types/report-log.type';
import dayjs, { Dayjs } from 'dayjs';
import { EChartColor } from '../pdf/reportPdfData';

export function convertData(
  data: any,
  unfixed?: boolean,
  convertToZero?: boolean,
) {
  if (convertToZero) {
    return '0.00';
  } else if (typeof data === 'number' || data === 0) {
    return !unfixed ? data.toFixed(2) : data;
  } else {
    return '-';
    // return !convertToNull ? undefined : null;
  }
}

function startOfDayInOffset(unixSeconds: number, timezone = 7) {
  const offsetSeconds = timezone * 3600;
  return (
    Math.floor((unixSeconds + offsetSeconds) / 86400) * 86400 - offsetSeconds
  );
}

function resolveContainerBounds(
  startTime: number,
  endTime: number,
  interval: EReportDataInterval,
  timezone?: number,
): [number, number] {
  // the buckets of a day start at the local midnight
  if (interval === EReportDataInterval.day) {
    return [
      startOfDayInOffset(startTime, timezone),
      startOfDayInOffset(endTime, timezone),
    ];
  }

  if (interval === EReportDataInterval.hour) {
    return [
      roundTimestamp(startTime, 'hour'),
      roundTimestamp(endTime, 'hour'),
    ];
  }

  if (interval === EReportDataInterval.minute) {
    return [
      roundTimestamp(startTime, 'minute'),
      roundTimestamp(endTime, 'minute'),
    ];
  }

  return [startTime, endTime];
}

/**
 * Tells whether a log bucket belongs to the requested report range.
 *
 * generateReportSummary used to answer this by building a container holding
 * every single second of the range and calling includes() on it, which is a
 * linear scan over 2.6 million entries for a 30 day report.
 */
export function createReportBucketFilter(params: {
  startTime: number;
  endTime: number;
  interval: EReportDataInterval;
  timezone?: number;
}): (timestamp: number) => boolean {
  // The month container is a list of month starts, so membership there means
  // "aligned to a month", not "inside the range".
  if (params.interval === EReportDataInterval.month) {
    const months = new Set(generateTimestampData(params));
    return (timestamp) => months.has(timestamp);
  }

  const [startTime, endTime] = resolveContainerBounds(
    params.startTime,
    params.endTime,
    params.interval,
    params.timezone,
  );

  return (timestamp) => timestamp >= startTime && timestamp <= endTime;
}

export function createBucketLookup<T>(
  section?: Record<string, T> | null,
): Map<number, T> {
  const lookup = new Map<number, T>();

  for (const key of Object.keys(section || {})) {
    lookup.set(Number(key), (section as Record<string, T>)[key]);
  }

  return lookup;
}

export function generateTimestampData(params: {
  startTime: number;
  endTime: number;
  intervalInSecond?: number;
  interval: EReportDataInterval;
  timezone?: number;
}): number[] {
  if (params.interval === EReportDataInterval.month) {
    const timezone = params.timezone ?? 7;
    const timestampArray: number[] = [];
    let currentTimestamp = reportDayjs(params.startTime, timezone).startOf(
      'month'
    );
    const endTimestamp = reportDayjs(params.endTime, timezone).startOf('month');

    while (
      currentTimestamp.isBefore(endTimestamp) ||
      currentTimestamp.isSame(endTimestamp)
    ) {
      timestampArray.push(currentTimestamp.unix());
      currentTimestamp = currentTimestamp.add(1, 'month');
    }

    return timestampArray;
  }

  const [startTime, endTime] = resolveContainerBounds(
    params.startTime,
    params.endTime,
    params.interval,
    params.timezone,
  );

  const currInterval = params.intervalInSecond || 60; // in seconds
  const timestampArray: number[] = [];
  let currentTimestamp = startTime;

  while (currentTimestamp <= endTime) {
    timestampArray.push(currentTimestamp);
    currentTimestamp += currInterval;
  }
  return timestampArray;
}

export function generateTimestampDateData(params: {
  startTime: number;
  endTime: number;
  intervalInSecond?: number;
  interval: EReportDataInterval;
}): string[] {
  const currInterval = params.intervalInSecond || 60; // in seconds
  const timestampArray: string[] = [];
  let currentTimestamp = params.startTime;

  while (currentTimestamp <= params.endTime) {
    timestampArray.push(dayjs(currentTimestamp * 1000).format('D/MM/YY HH:mm'));
    currentTimestamp += currInterval;
  }
  return timestampArray;
}

export function toHHMMSS(seconds?: number | null) {
  if (!seconds) {
    return '00:00:00';
  }
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;

  return [hours, minutes, remainder]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function createBucketDateFormatter(interval: string, timezone: number) {
  const offsetMs = timezone * 60 * 60 * 1000;
  const pad = (value: number) => String(value).padStart(2, '0');

  return (timestamp: number) => {
    const shifted = new Date(timestamp * 1000 + offsetMs);
    const month = MONTH_LABELS[shifted.getUTCMonth()];
    const year = shifted.getUTCFullYear();

    if (interval === 'month') {
      return `${month} ${year}`;
    }

    const date = `${pad(shifted.getUTCDate())} ${month} ${year}`;

    if (interval === 'day' || interval === 'week') {
      return date;
    }

    return `${date} ${pad(shifted.getUTCHours())}:${pad(
      shifted.getUTCMinutes(),
    )}`;
  };
}

export const toHHMM = (seconds: number) => {
  let hours: string | number = Math.floor(seconds / 3600);
  let minutes: string | number = Math.floor((seconds % 3600) / 60);

  hours = hours < 10 ? '0' + hours : hours;
  minutes = minutes < 10 ? '0' + minutes : minutes;

  return hours + ':' + minutes;
};

function toDegreesMinutesAndSeconds(coordinate: any) {
  const absolute = Math.abs(coordinate);
  const degrees = Math.floor(absolute);
  const minutesNotTruncated = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesNotTruncated);
  const seconds = Math.floor((minutesNotTruncated - minutes) * 60);

  return `${degrees}° ${minutes}' ${seconds}''`;
}

export const latToDMS = (value: any) => {
  if (!value) return '-';
  const cardinal = value > 0 ? 'N' : 'S';
  return `${toDegreesMinutesAndSeconds(value)} ${cardinal}`;
};

export const longToDMS = (value: any) => {
  if (!value) return '-';
  const cardinal = value > 0 ? 'E' : 'W';
  return `${toDegreesMinutesAndSeconds(value)} ${cardinal}`;
};

export const hChartConfig = {
  chart: {
    zoomType: 'xy',
    height: 480,
    width: null,
    reflow: true,
    animation: false,
  },
  title: {
    text: '',
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    series: {
      groupPadding: 0.2,
      pointPadding: 0,
      animation: false,
    },
  },
  tooltip: {
    shared: true,
  },
};

export const hColors = {
  orange: '#ffa216',
  lightOrange: '#FFC977',
  yellow: '#FFBC00',
  yellowOpacity: '#ffdc79',
  pink: '#F34584',
  red: '#FE4545',
  purple: '#6F57E9',
  purpleOpacity: 'rgb(111 87 233 / 80%)',
  teal: '#02CCCC',
  tealOpacity: 'rgb(2 204 204 / 80%)',
  green: '#35CD69',
  lightGreen: 'lime',
  darkBlue: '#163E8A',
  blue: '#1b71f3',
  lightBlue: '#6BA6FF',
  black: '#000000',
  lightBlack: 'rgba(0,0,0,.1)',
  gray: '#808080',
  limeYellow: '#e2e600',
};

export const zoneChartType = ['line', 'spline', 'areaspline', 'arealine'];

interface IDataZone {
  data: any[];
  color: string;
  type: string;
}

export function fillZerosWithAverage(arr: any[]) {
  for (let i = 1; i < arr.length - 1; i++) {
    if (arr[i] === 0 && arr[i - 1] > 0 && arr[i + 1] > 0) {
      const average = (arr[i - 1] + arr[i + 1]) / 2;
      arr[i] = average;
    }
  }
  return arr;
}

export function getDatesBetween(startDate: Dayjs, endDate: Dayjs) {
  if (startDate.isAfter(endDate)) {
    return [];
  }

  var dates = [];

  while (startDate.isBefore(endDate) || startDate.isSame(endDate)) {
    dates.push(startDate.format('YYYY-MM-DD'));
    startDate.add(1, 'days');
  }

  return dates;
}

export function hexToRgba(hex: string, alpha: number) {
  let rgba = `rgba(0,0,0,${alpha})`;
  if (hex.includes('#')) {
    // Remove any leading '#' if present
    hex = hex.replace(/^#/, '');

    // Parse the hexadecimal values for Red, Green, and Blue
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Ensure alpha is a valid value between 0 and 1
    alpha = Math.min(1, Math.max(0, alpha));

    // Create the RGBA string
    rgba = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else if (hex.includes('rgb')) {
    rgba = hex;
  }
  return rgba;
}

export function generateSeriesDataZone(params: IDataZone) {
  return params.data.reduce((nullIndices, currentValue, index) => {
    if (currentValue === null) {
      if (nullIndices.length === 0) {
        nullIndices.push({
          value: index - 1 < 0 ? 0 : index - 1,
          color: params.color ? hexToRgba(params.color, 0.6) : '#000000',
          dashStyle: 'shortdot',
          fillColor:
            params.type.includes('area') && params.color
              ? hexToRgba(params.color, 0.2)
              : '#000000',
        });
      }
      // else {
      //   if (index - nullIndices[nullIndices.length - 1].value > 1) {
      //     nullIndices.push({ value: index - 1 });
      //   }
      // }

      nullIndices.push({
        value: index,
        color: params.color ? hexToRgba(params.color, 0.6) : '#000000',
        dashStyle: 'shortdot',
        fillColor:
          params.type.includes('area') && params.color
            ? hexToRgba(params.color, 0.2)
            : '#000000',
      });
    }
    return nullIndices;
  }, []);
}

export function fillNullValues(arr: any[]) {
  const res = arr.reduce((result, currentValue, index, array) => {
    if (currentValue === null) {
      let prev = null;
      let next = null;

      for (let j = index - 1; j >= 0; j--) {
        if (array[j] !== null) {
          prev = array[j];
          break;
        }
      }

      for (let j = index + 1; j < array.length; j++) {
        if (array[j] !== null) {
          next = array[j];
          break;
        }
      }

      if (next === null) {
        result.push(prev);
      } else if (prev === null) {
        result.push(next);
      } else {
        result.push((prev + next) / 2);
      }
    } else {
      result.push(currentValue);
    }
    return result;
  }, []);
  return res;
  // return res.map((item: number) => (item ? Number(item.toFixed(2)) : item));
}

export function fillNullValuesWithAverage(arr: any[]) {
  const result = [...arr];
  let nullIndices: number[] = [];
  let nullIndices2: number[] = [];
  if (result[0] === null) {
    result[0] = 0;
  }

  if (result[result.length - 1] === null) {
    result[result.length - 1] = 0;
  }

  for (let i = 1; i < result.length - 1; i++) {
    if (result[i] === null) {
      nullIndices.push(i);
    }
  }

  for (let i = 1; i < result.length - 1; i++) {
    if (result[i] === null) {
      const prev = result[i - 1];
      let nextIndex = i + 1;
      while (nextIndex < result.length && result[nextIndex] === null) {
        nextIndex++;
      }
      const next = result[nextIndex];

      if (next !== undefined) {
        const average = Math.floor((prev + next) / 2);
        while (i < nextIndex) {
          result[i] = average;
          i++;
        }
      }
    }
  }

  return { result, nullIndices };
}

export function generateDashStyles(indices: number[], totalLength: number) {
  const result = [];

  let i = 0;
  while (i < indices.length) {
    const current = indices[i];
    const next = indices[i + 1];

    // Handle case for starting sequence at 0
    if (current === 0) {
      let endSequence = current;
      while (indices[i + 1] === indices[i] + 1) {
        endSequence = indices[i + 1];
        i++;
      }
      result.push({
        value: endSequence,
        dashStyle: 'Dot',
      });
      i++; // Move to next value after the sequence
      continue;
    }

    // Handle case for sequences not starting at 0
    if (next && next === current + 1) {
      let startSequence = current;
      let endSequence = current;
      while (indices[i + 1] === indices[i] + 1) {
        endSequence = indices[i + 1];
        i++;
      }
      // Add entry before the sequence with 'Dot' style
      result.push({
        value: startSequence - 1 >= 0 ? startSequence - 1 : 0,
        dashStyle: 'Solid',
      });
      // Add entry after the sequence with 'Solid' style
      result.push({
        value:
          endSequence + 1 < totalLength ? endSequence + 1 : totalLength - 1,
        dashStyle: 'Dot',
      });
    }

    // Handle single value case (like 12)
    else if (!next || next !== current + 1) {
      result.push({
        value: current - 1 >= 0 ? current - 1 : 0,
        dashStyle: 'Solid',
      });
      result.push({
        value: current,
        dashStyle: 'Dot',
      });
    }

    i++;
  }

  return result;
}

export const toKM = (value: number) => {
  if (!value) {
    return 0;
  }
  return value / 1000;
};

export const toMiles = (value: number) => {
  if (!value) {
    return 0;
  }
  return value * 0.00062137;
};

export const toNM = (value: number) => {
  if (!value) {
    return 0;
  }
  return value * 0.00053996;
};

export const toKnot = (value: number) => {
  if (!value) {
    return 0;
  }
  return value / 1.852;
};

export function generateDataSeries(params: {
  timestamps: number[];
  key: string | string[];
  seriesOption: {
    name: string;
    type: string;
    yAxis: number;
    zIndex: number;
    marker?: any;
    lineWidth?: number;
    color?: string;
    fillColor?: string;
    lineColor?: string;
    tooltip?: any;
    fillOpacity?: number;
  };
  dataLog?: ILogFlowmeter;
}) {
  const data: any[] = [];
  let obj: any;

  const { timestamps, key, seriesOption, dataLog } = params;

  if (dataLog && Object.keys(dataLog).length) {
    obj = dataLog as any;
  }

  const getNestedValue = (obj: any, keyPath: string | string[]) => {
    if (typeof keyPath === 'string') {
      return obj[keyPath];
    }
    return keyPath.reduce((prev, curr) => (prev ? prev[curr] : null), obj);
  };

  if (obj && Object.keys(obj).length) {
    const bucketLookup = createBucketLookup(obj);
    timestamps.forEach((timestamp) => {
      const bucket = bucketLookup.get(timestamp);
      if (bucket && key) {
        data.push(getNestedValue(bucket, key));
      } else {
        data.push(null);
      }
    });
  }

  return {
    ...seriesOption,
    fillColor: seriesOption.fillColor || undefined,
    data: data,
    marker: seriesOption.marker || {
      enabled: false,
    },
    lineWidth: seriesOption.lineWidth || 2,
  };
}

export function generateDataSeriesTable(params: {
  timestamps: number[];
  key: string | string[];
  fm?: ILogFlowmeter;
  rpm?: ILogRpm;
  gps?: ILogGPS;
  ae?: ILogAE;
}) {
  const data: any[] = [];
  let obj: any;

  const { timestamps, key, fm, rpm, gps, ae } = params;

  if (fm && Object.keys(fm).length) {
    obj = fm as ILogFlowmeter;
  } else if (rpm && Object.keys(rpm).length) {
    obj = rpm as ILogRpm;
  } else if (gps && Object.keys(gps).length) {
    obj = gps as ILogGPS;
  } else if (ae && Object.keys(ae).length) {
    obj = ae as ILogAE;
  }

  const getNestedValue = (obj: any, keyPath: string | string[]) => {
    if (typeof keyPath === 'string') {
      return obj[keyPath];
    }
    return keyPath.reduce((prev, curr) => (prev ? prev[curr] : null), obj);
  };

  const bucketLookup = createBucketLookup<any>(obj);

  timestamps.forEach((timestamp) => {
    const bucket = bucketLookup.get(timestamp);
    if (bucket && key) {
      data.push(getNestedValue(bucket, key));
    } else {
      data.push(undefined);
    }
  });

  return data;
}

export function calculateGpsSummary(
  logs: ILogReport,
  units?: {
    distanceUnit: string;
    speedUnit: string;
    cruiseUnit: string;
  },
) {
  let totalSpeed = 0;
  let totalCruise = 0;
  let totalDistance = 0;
  let newTotalDistance = 0;
  let countData = 0;

  if (logs && logs.gps && Object.keys(logs.gps).length) {
    Object.values(logs.gps).forEach((item) => {
      // a bucket's speed is its first minute, its stats cover every minute
      if (item.stats?.countGps > 0) {
        countData += item.stats.countGps;
        totalSpeed += item.stats.sumSpeed || 0;
      } else {
        countData += 1;
        totalSpeed += item.speed || 0;
      }
      // totalCruise += item.speed > 3 ? item.distance : 0;
      totalCruise += item.stats?.sumDistance || 0;
    });

    const firstKey = Object.keys(logs.gps)[0];
    const lastKey = Object.keys(logs.gps)[Object.keys(logs.gps).length - 1];
    newTotalDistance = haversineDistance(
      logs.gps[firstKey].stats.firstLatitude,
      logs.gps[firstKey].stats.firstLongitude,
      logs.gps[lastKey].stats.lastLatitude,
      logs.gps[lastKey].stats.lastLongitude,
    );

    if (!units) {
      totalCruise = toNM(totalCruise);
      totalDistance = toNM(newTotalDistance);
      totalSpeed = totalSpeed;
    } else {
      if (units.distanceUnit === 'MILES') {
        totalDistance = toMiles(newTotalDistance);
      } else if (units.distanceUnit === 'KM') {
        totalDistance = toKM(newTotalDistance);
      } else if (units.distanceUnit === 'NM') {
        totalDistance = toNM(newTotalDistance);
      }

      if (units.cruiseUnit === 'KM') {
        totalCruise = toKM(totalCruise);
      } else if (units.cruiseUnit === 'NM') {
        totalCruise = toNM(totalCruise);
      } else if (units.cruiseUnit === 'MILES') {
        totalCruise = toMiles(totalCruise);
      }

      if (units.speedUnit === 'KNOT') {
        totalSpeed = toKnot(totalSpeed);
      } else {
        totalSpeed = totalSpeed;
      }
    }

    return {
      avgSpeed: (totalSpeed / countData).toFixed(2),
      totalCruise: totalCruise.toFixed(2),
      totalDistance: totalDistance.toFixed(2),
    };
  }

  return {
    avgSpeed: '0',
    totalCruise: '0',
    totalDistance: '0',
  };
}

/**
 * calculateGpsSummary's strings from the gps of GET /report-log/summary
 * (meter, speed in the unit of the log). Same as formatGpsSummary in
 * ramus-app-be report-summary.query.ts.
 */
export function formatGpsSummary(
  gps: IReportSummary['gps'] | undefined,
  units?: {
    distanceUnit: string;
    speedUnit: string;
    cruiseUnit: string;
  },
) {
  if (!gps?.count) {
    return { avgSpeed: '0', totalCruise: '0', totalDistance: '0' };
  }

  const distanceIn = (unit: string | undefined, meter: number) => {
    if (!units || unit === 'NM') return toNM(meter);
    if (unit === 'KM') return toKM(meter);
    if (unit === 'MILES') return toMiles(meter);
    return 0;
  };
  const speed =
    units?.speedUnit === 'KNOT' ? toKnot(gps.avgSpeed) : gps.avgSpeed;

  return {
    avgSpeed: speed.toFixed(2),
    totalCruise: distanceIn(units?.cruiseUnit, gps.totalCruise).toFixed(2),
    totalDistance: distanceIn(units?.distanceUnit, gps.totalDistance).toFixed(
      2,
    ),
  };
}

/**
 * Liter of a flowmeter or AE (port, ae1, ...) in a bucket of the report log.
 * The backend puts it on its labels as stats.fuelConsLiter, from the query of
 * the summary, so the rows add up to the summary whatever the interval. A log
 * without it (ramus2, migration) falls back to the stats of the bucket.
 */
export function getBucketFuelCons(
  bucket: Record<string, any> | undefined,
  base: string,
  interval: string,
): number {
  const inObj = bucket?.[`${base}In`];
  const outObj = bucket?.[`${base}Out`];
  const statObj = bucket?.[base];

  const liter = [inObj, statObj, outObj].find(
    (item) => typeof item?.stats?.fuelConsLiter === 'number',
  )?.stats.fuelConsLiter;
  if (liter !== undefined) return liter;

  if (inObj && outObj) {
    // flow rate is liter per hour, a minute row is 1/60 of it
    return interval === EReportDataInterval.minute
      ? ((inObj.volumeFlowrate || 0) - (outObj.volumeFlowrate || 0)) / 60
      : ((inObj.stats?.sumVolumeFlowrate || 0) -
          (outObj.stats?.sumVolumeFlowrate || 0)) /
          60;
  }
  if (inObj) return inObj.stats?.fuelConsVolume || 0;
  if (outObj) return outObj.stats?.fuelConsVolume || 0;
  if (statObj) return statObj.stats?.sumFuelCons || 0;
  return 0;
}

export function generateDeviceOptions(devices: DeviceProperties[]) {
  const resDevices = devices.map((item) => ({
    uniqueId: item.uniqueId,
    deviceType: item.deviceType.name.toLocaleLowerCase(),
  }));

  let result: { type: EDeviceType; devices: string[] }[] = [];
  resDevices.forEach((item) => {
    if (item.deviceType === EDeviceType.FLOWMETER) {
      const found = result.find((item) => item.type === EDeviceType.FLOWMETER);
      if (item.uniqueId.includes('fm-1')) {
        if (!found) {
          result.push({
            type: EDeviceType.FLOWMETER,
            devices: ['port'],
          });
        } else {
          result = result.map((item) => {
            if (
              item.type === EDeviceType.FLOWMETER &&
              !item.devices.includes('port')
            ) {
              return {
                ...item,
                devices: [...item.devices, 'port'],
              };
            }
            return item;
          });
        }
      } else if (item.uniqueId.includes('fm-2')) {
        if (!found) {
          result.push({
            type: EDeviceType.FLOWMETER,
            devices: ['starboard'],
          });
        } else {
          result = result.map((item) => {
            if (
              item.type === EDeviceType.FLOWMETER &&
              !item.devices.includes('starboard')
            ) {
              return {
                ...item,
                devices: [...item.devices, 'starboard'],
              };
            }

            return item;
          });
        }
      } else if (item.uniqueId.includes('fm-3')) {
        if (!found) {
          result.push({
            type: EDeviceType.FLOWMETER,
            devices: ['center'],
          });
        } else {
          result = result.map((item) => {
            if (
              item.type === EDeviceType.FLOWMETER &&
              !item.devices.includes('center')
            ) {
              return {
                ...item,
                devices: [...item.devices, 'center'],
              };
            }

            return item;
          });
        }
      }
    } else if (item.deviceType.includes(EDeviceType.AE)) {
      let deviceId: string = '';
      const ids = item.uniqueId.split('-');
      if (ids && ids.length) {
        deviceId = ids[1] + '_' + ids[2];
      }
      const found = result.find((item) => item.type.includes(EDeviceType.AE));
      if (deviceId) {
        if (!found) {
          result.push({
            type: EDeviceType.AE,
            devices: [deviceId],
          });
        } else {
          result = result.map((item) => {
            if (
              item.type === EDeviceType.AE &&
              !item.devices.includes(deviceId)
            ) {
              return {
                ...item,
                devices: [...item.devices, deviceId],
              };
            }

            return item;
          });
        }
      }
    } else if (item.deviceType === EDeviceType.RPM) {
      const found = result.find((item) => item.type === EDeviceType.RPM);
      if (item.uniqueId.includes('rpm-1')) {
        if (!found) {
          result.push({
            type: EDeviceType.RPM,
            devices: ['port'],
          });
        } else {
          result = result.map((item) => {
            if (item.type === EDeviceType.RPM) {
              return {
                ...item,
                devices: [...item.devices, 'port'],
              };
            }
            return item;
          });
        }
      } else if (item.uniqueId.includes('rpm-2')) {
        if (!found) {
          result.push({
            type: EDeviceType.RPM,
            devices: ['starboard'],
          });
        } else {
          result = result.map((item) => {
            if (item.type === EDeviceType.RPM) {
              return {
                ...item,
                devices: [...item.devices, 'starboard'],
              };
            }
            return item;
          });
        }
      }
    } else if (item.deviceType === EDeviceType.GPS) {
      result.push({
        type: EDeviceType.GPS,
        devices: ['gps'],
      });
    } else if (item.deviceType === EDeviceType.GATEWAY) {
      result.push({
        type: EDeviceType.GATEWAY,
        devices: ['gateway'],
      });
    }
  });

  return result;
}

export function convertIntervalToSecond(interval: string) {
  let intervalInSecond = 0;

  if (interval === EReportDataInterval.minute) {
    intervalInSecond = 60;
  } else if (interval === EReportDataInterval.hour) {
    intervalInSecond = 60 * 60;
  } else if (interval === EReportDataInterval.week) {
    intervalInSecond = 60 * 60 * 24 * 7;
  } else if (interval === EReportDataInterval.month) {
    intervalInSecond = 60 * 60 * 24 * 30;
  } else {
    intervalInSecond = 60 * 60 * 24;
  }

  return intervalInSecond;
}

export function generateReportSummary(props: {
  startAt: number;
  endAt: number;
  interval: EReportDataInterval;
  logs: ILogReport;
  timezone?: number;
}) {
  let { startAt, endAt, interval, logs, timezone } = props;
  let res: any = {
    totalEngineFuelCons: 0,
    totalAEFuelCons: 0,
    totalAERunningTime: 0,
    portRPMCount: 0,
    portRPM: 0,
    portRunningHour: 0,
    portTotalFuel: 0,
    portAverageFlowCount: 0,
    portAverageFlow: 0,
    starboardRPMCount: 0,
    starboardRPM: 0,
    starboardRunningHour: 0,
    starboardTotalFuel: 0,
    starboardAverageFlowCount: 0,
    starboardAverageFlow: 0,
    centerRPMCount: 0,
    centerRPM: 0,
    centerRunningHour: 0,
    centerTotalFuel: 0,
    centerAverageFlowCount: 0,
    centerAverageFlow: 0,
  };

  let resAEData: any = {};

  const isInsideRange = createReportBucketFilter({
    startTime: startAt,
    endTime: endAt,
    interval,
    timezone,
  });

  if (logs && Object.keys(logs).length) {
    const fm = logs['flowmeter'];
    const rpm = logs['rpm'];
    const ae = logs['ae'];

    if (fm && Object.keys(fm).length) {
      Object.keys(fm).forEach((timestampKey) => {
        if (fm && fm[timestampKey] && isInsideRange(Number(timestampKey))) {
          const fmKeys = Object.keys(fm[timestampKey]).filter(
            (k) =>
              ![
                'totalFuelCons',
                'totalFuelConsVolume',
                'totalFuelConsumption',
                'totalRunningTime',
              ].includes(k),
          );

          const processedBases = new Set<string>();

          fmKeys.forEach((k) => {
            const base = k.replace(/(In|Out)$/, '');

            if (processedBases.has(base)) return;
            processedBases.add(base);

            const inObj = fm[timestampKey][`${base}In`];
            const outObj = fm[timestampKey][`${base}Out`];
            const statObj = fm[timestampKey][base];

            if (!res[`${base}TotalFuel`]) {
              res[`${base}TotalFuel`] = 0;
            }

            if (!res[`fm${base}TotalFuel`]) {
              res[`fm${base}TotalFuel`] = 0
            }

            if (inObj && inObj.stats && res[`${base}AverageFlow`] !== undefined) {
              res[`${base}AverageFlow`] += inObj.stats.sumVolumeFlowrate || 0;
              res[`${base}AverageFlowCount`] += inObj.stats.countVolumeFlowrate || 0;
            }

            if (inObj && outObj) {
              // const fuelCons =
              //   ((inObj.volumeFlowrate || 0) - (outObj.volumeFlowrate || 0)) /
              //   (interval === EReportDataInterval.minute ? 60 : 1);

              let fuelCons = 0
              if (interval === EReportDataInterval.minute) {
                fuelCons = ((inObj.volumeFlowrate || 0) - (outObj.volumeFlowrate || 0)) / 60;
              } else {
                fuelCons = ((inObj.stats?.sumVolumeFlowrate || 0) - (outObj.stats?.sumVolumeFlowrate || 0)) / 60;
              }

              res[`fm${base}TotalFuel`] += fuelCons;
              res['totalEngineFuelCons'] += fuelCons;
            } else if (inObj) {
              const fuelCons = inObj.stats?.fuelConsVolume || 0;
              res[`fm${base}TotalFuel`] += fuelCons;
              res['totalEngineFuelCons'] += fuelCons;
            } else if (statObj) {
              const fuelCons = statObj.stats?.sumFuelCons || 0;
              res[`fm${base}TotalFuel`] += fuelCons;
              res['totalEngineFuelCons'] += fuelCons;
            }
          });
        }
      });
    }

    if (rpm && Object.keys(rpm).length) {
      Object.keys(rpm).forEach((key) => {
        if (rpm && rpm[key] && isInsideRange(Number(key))) {
          // port
          if (rpm[key]['port']) {
            res.portRPM += rpm[key]['port'].stats?.sumRpm || 0;
            res.portRPMCount += rpm[key]['port'].stats?.countGT || 0;
            res.portRunningHour += rpm[key]['port'].runningTime || 0;
          }

          // starboard
          if (rpm[key]['starboard']) {
            res.starboardRPM += rpm[key]['starboard'].stats?.sumRpm || 0;
            res.starboardRPMCount += rpm[key]['starboard'].stats?.countGT || 0;
            res.starboardRunningHour += rpm[key]['starboard'].runningTime || 0;
          }

          // center
          if (rpm[key]['center']) {
            res.centerRPM += rpm[key]['center'].stats?.sumRpm || 0;
            res.centerRPMCount += rpm[key]['center'].stats?.countGT || 0;
            res.centerRunningHour += rpm[key]['center'].runningTime || 0;
          }
        }
      });
    }

    if (ae && Object.keys(ae).length) {
      Object.keys(ae).forEach((timestampKey) => {
        if (ae && ae[timestampKey] && isInsideRange(Number(timestampKey))) {
          const aeKeys = Object.keys(ae[timestampKey]).filter(
            (k) =>
              ![
                'totalFuelCons',
                'totalFuelConsVolume',
                'totalFuelConsumption',
                'totalRunningTime',
              ].includes(k),
          );

          const processedBases = new Set<string>();

          aeKeys.forEach((k) => {
            const base = k.replace(/(In|Out)$/, '');

            if (processedBases.has(base)) return;
            processedBases.add(base);

            const inObj = ae[timestampKey][`${base}In`];
            const outObj = ae[timestampKey][`${base}Out`];
            const statObj = ae[timestampKey][base];

            if (!res[`${base}TotalFuel`]) {
              res[`${base}TotalFuel`] = 0;
            }

            if (!res[`${base}RunningHour`]) {
              res[`${base}RunningHour`] = 0;
            }

            if (inObj && outObj) {
              // a minute holds the flow rate (L/h) of that minute; longer buckets sum
              // the flow rate of every minute in them, both are liters after / 60
              const fuelCons =
                interval === EReportDataInterval.minute
                  ? ((inObj.volumeFlowrate || 0) - (outObj.volumeFlowrate || 0)) / 60
                  : ((inObj.stats?.sumVolumeFlowrate || 0) -
                      (outObj.stats?.sumVolumeFlowrate || 0)) /
                    60;

              res[`${base}TotalFuel`] += fuelCons;
              res[`${base}RunningHour`] += inObj.runningTime || 0;
              res['totalAEFuelCons'] += fuelCons;
              res['totalAERunningTime'] += inObj.runningTime || 0;
            } else if (inObj) {
              const fuelCons = inObj.stats?.fuelConsVolume || 0;
              res[`${base}TotalFuel`] += fuelCons;
              res[`${base}RunningHour`] += inObj.runningTime || 0;
              res['totalAEFuelCons'] += fuelCons;
              res['totalAERunningTime'] += inObj.runningTime || 0;
            } else if (statObj) {
              const fuelCons = statObj.stats?.sumFuelCons || 0;
              res[`${base}TotalFuel`] += fuelCons;
              res[`${base}RunningHour`] += statObj.runningTime || 0;
              res['totalAEFuelCons'] += fuelCons;
              res['totalAERunningTime'] += statObj.runningTime || 0;
            }
          });
        }
      });
    }
  }

  return {
    ...res,
    ...resAEData,
    // rpm
    portRPM:
      res.portRPM !== 0 && res.portRPMCount !== 0
        ? res.portRPM / res.portRPMCount
        : 0,
    starboardRPM:
      res.starboardRPM !== 0 && res.starboardRPMCount !== 0
        ? res.starboardRPM / res.starboardRPMCount
        : 0,
    centerRPM:
      res.centerRPM !== 0 && res.centerRPMCount !== 0
        ? res.centerRPM / res.centerRPMCount
        : 0,
    // average flow
    portAverageFlow:
      res.portAverageFlow !== 0 && res.portAverageFlowCount !== 0
        ? res.portAverageFlow / res.portAverageFlowCount
        : 0,
    starboardAverageFlow:
      res.starboardAverageFlow !== 0 && res.starboardAverageFlowCount !== 0
        ? res.starboardAverageFlow / res.starboardAverageFlowCount
        : 0,
    centerAverageFlow:
      res.centerAverageFlow !== 0 && res.centerAverageFlowCount !== 0
        ? res.centerAverageFlow / res.centerAverageFlowCount
        : 0,
  };
}

function mergeArrayOp(params: {
  data: any[];
  sumData?: boolean;
  mergeFirstName?: boolean;
}) {
  return Object.values(
    params.data.reduce((acc: any, item) => {
      let name = item.name;
      if (params.mergeFirstName) {
        name = item.name.split(' ')[0];
      }
      if (!acc[name]) {
        acc[name] = {
          name: !params.mergeFirstName ? item.name : name,
          runningTime: 0,
          consRate: 0,
        };
      }

      if (params.sumData) {
        acc[name].runningTime += item.runningTime;
        acc[name].consRate += item.consRate;
      }

      return acc;
    }, {}),
  );
}

export const CLUTCH_OPERATION_MODE_KEYS = [
  'clutchPort',
  'clutchStarboard',
  'clutchCenter',
] as const;

export function getPresentOperationModeKeys(
  operationMode: ILogOperationMode | undefined,
  keys: readonly string[],
): string[] {
  if (!operationMode) return [];

  const present = new Set<string>();
  Object.values(operationMode).forEach((bucket) => {
    if (!bucket) return;
    keys.forEach((key) => {
      if (bucket[key]) present.add(key);
    });
  });

  return keys.filter((key) => present.has(key));
}

export function generateReportOperationModeSummary(props: {
  startAt: number;
  endAt: number;
  interval: EReportDataInterval;
  logs: ILogReport;
  opModes: OperationModeDetailProperties[];
  timezone: number;
  engine?: 'port' | 'starboard';
  engines?: string[];
}) {
  let { startAt, endAt, interval, logs, opModes, timezone, engine, engines: engineKeys } =
    props;

  const newOpModes = mergeArrayOp({ data: opModes });

  let results: any[] = [];

  const isInsideRange = createReportBucketFilter({
    startTime: startAt,
    endTime: endAt,
    interval,
    timezone,
  });

  const engines = engineKeys?.length
    ? engineKeys
    : engine
      ? [engine]
      : ['port', 'starboard'];
  const runningTimeDivisor = engineKeys?.length
    ? engineKeys.length
    : engine
      ? 1
      : 2;

  if (logs && Object.keys(logs).length && opModes.length) {
    const opMode = logs['operation_mode'];

    if (opMode && Object.keys(opMode).length) {
      Object.keys(opMode).forEach((key) => {
        engines.forEach((currentEngine) => {
          const operationMode = opMode[key]?.[currentEngine];

          if (!operationMode || !isInsideRange(Number(key))) return;

          // an hour or a day bucket carries the modes of its minutes
          if (operationMode.modes) {
            results.push(...operationMode.modes);
          } else if (operationMode.mode !== '-') {
            results.push({
              name: operationMode.mode,
              runningTime: operationMode.runningTime,
              consRate: operationMode.consRate ?? 0,
            });
          }
        });
      });
    }
  }

  let newResults: any[] = [];

  if (results.length) {
    newResults = mergeArrayOp({
      data: [...newOpModes, ...results],
      sumData: true,
      mergeFirstName: true,
    });
  }

  return newResults.map((item) => ({
    ...item,
    runningTime: item.runningTime / runningTimeDivisor,
  }));
}

export function convertExcelTime(excelTime: number) {
  let totalSeconds = Math.round(excelTime * 24 * 60 * 60);

  let hours = Math.floor(totalSeconds / 3600);
  let minutes = Math.floor((totalSeconds % 3600) / 60);
  let seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, '0'))
    .join(':');
}

// the PDF chart schemas use it too, so it lives with them
export { EChartColor };

export const defaultYAxis = [
  {
    key: 'rpm',
    min: 0,
    color: EChartColor.black,
    text: 'RPM',
  },
  {
    key: 'clutch',
    min: 0,
    color: EChartColor.black,
    text: 'Clutch',
  },
  {
    key: 'fuel',
    min: 0,
    color: EChartColor.yellow,
    text: 'Liters',
  },
  {
    key: 'speed',
    min: 0,
    color: EChartColor.pink,
    text: 'Knot',
    opposite: true,
  },
  {
    key: 'time',
    min: 0,
    color: EChartColor.black,
    text: 'Minute',
    fixed: true,
    opposite: true,
  },
  {
    key: 'runningTime',
    min: 0,
    color: EChartColor.black,
    text: 'Running Time',
    fixed: true,
    opposite: true,
  },
  {
    key: 'altitude',
    min: 0,
    color: EChartColor.black,
    text: 'Altitude',
    fixed: true,
  },
  {
    key: 'distance',
    min: 0,
    color: EChartColor.purple,
    text: 'KM',
    fixed: true,
    opposite: true,
  },
  {
    key: 'density',
    min: 0,
    color: EChartColor.pink,
    text: 'Density',
    fixed: true,
    opposite: false,
  },
  {
    key: 'volumeTotal',
    min: 0,
    color: EChartColor.blue,
    text: 'Volume Total',
    fixed: true,
    opposite: false,
  },
  {
    key: 'volumeFlowRate',
    min: 0,
    color: EChartColor.purple,
    text: 'Volume Flow Rate',
    fixed: true,
    opposite: false,
  },
  {
    key: 'massFlowRate',
    min: 0,
    color: EChartColor.yellow,
    text: 'Mass Flow Rate',
    fixed: true,
    opposite: true,
  },
  {
    key: 'massTotal',
    min: 0,
    color: EChartColor.green,
    text: 'Mass Total',
    fixed: true,
    opposite: true,
  },
  {
    key: 'temperature',
    min: 0,
    color: EChartColor.blueDark,
    text: 'Temperature',
    fixed: true,
    opposite: true,
  },
  {
    key: 'ampere',
    min: 0,
    color: EChartColor.pink,
    text: 'Ampere',
    fixed: true,
    opposite: true,
  },
  {
    key: 'voltage',
    min: 0,
    color: EChartColor.teal,
    text: 'Voltage',
    fixed: true,
    opposite: true,
  },
  {
    key: 'cpuUsage',
    min: 0,
    color: EChartColor.pink,
    text: 'CPU Usage',
    fixed: true,
    opposite: false,
  },
  {
    key: 'memoryFree',
    min: 0,
    color: EChartColor.purple,
    text: 'Memory Free',
    fixed: true,
    opposite: false,
  },
  {
    key: 'spaceFree',
    min: 0,
    color: EChartColor.teal,
    text: 'Space Free',
    fixed: true,
    opposite: true,
  },
  {
    key: 'signalStrength',
    min: 0,
    color: EChartColor.black,
    text: 'Signal Strength',
    fixed: true,
    opposite: true,
  },
];

export function generateDataCompletion(obj: any, type: string, keyObj: string) {
  let count = 0;
  let lastTimestamp = '';
  let currentDataCompletion = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key][keyObj] || typeof obj[key][keyObj] === 'number') {
      count += 1;
      lastTimestamp = key;
    }
  });
  currentDataCompletion = {
    type,
    count,
    lastTimestamp,
  };

  return currentDataCompletion;
}

export const defaultGPS = {
  gps_coordinate: '-',
  gps_latitude: '-',
  gps_longitude: '-',
  gps_heading: '-',
  gps_speed: '-',
  gps_distance: '-',
};

export const defaultAE = {
  ae1_RunningTime: '-',
  ae1_FuelConsumption: '-',
  ae2_RunningTime: '-',
  ae2_FuelConsumption: '-',
  ae3_RunningTime: '-',
  ae3_FuelConsumption: '-',
};

export const defaultRPM = {
  port_Rpm: '-',
  port_EngineStatus: '-',
  port_RunningTime: '-',
  starboard_Rpm: '-',
  starboard_EngineStatus: '-',
  starboard_RunningTime: '-',
  center_Rpm: '-',
  center_EngineStatus: '-',
  center_RunningTime: '-',
};

export const defaultFlowmeter = {
  port_Rpm: '-',
  port_EngineStatus: '-',
  port_FuelCons: '-',
  port_VolumeTotal: '-',
  port_InFlow: '-',
  port_Temperature: '-',
  port_InDensity: '-',
  starboard_Rpm: '-',
  starboard_EngineStatus: '-',
  starboard_FuelCons: '-',
  starboard_VolumeTotal: '-',
  starboard_InFlow: '-',
  starboard_Temperature: '-',
  starboard_InDensity: '-',
  center_Rpm: '-',
  center_EngineStatus: '-',
  center_FuelCons: '-',
  center_VolumeTotal: '-',
  center_InFlow: '-',
  center_Temperature: '-',
  center_InDensity: '-',
};

export const checkDataType = () => {
  let dataType = 0;
  const host = window.location.host;

  if (host.includes('pinisi.asia')) {
    dataType = 1;
  }

  return dataType;
};

export function mappingAssetAlias(assets: AssetProperties[]) {
  return assets.map((item) => {
    if (item.assetId) {
      return {
        ...item,
        assetId: item.aliasId,
      };
    } else {
      return item;
    }
  });
}

export function validateDeviceAvailability(
  devices: DeviceProperties[],
  assetType: string,
) {
  const hasDevice = (pattern: string) =>
    devices.some((device) => device.uniqueId?.includes(pattern));

  const hasAnyRPM = devices.some((device) => device.uniqueId?.includes('rpm-'));
  const hasAnyClutch = devices.some((device) =>
    device.uniqueId?.includes('clutch-'),
  );

  const hasAnyFM = devices.some((device) => device.uniqueId?.includes('fm-'));

  return {
    hasAnyRPM,
    hasAnyClutch,
    hasAnyFM,
    hasFMPort: hasDevice('fm-1'),
    hasRPMPort: hasDevice('rpm-1'),
    hasFMStarboard: hasDevice('fm-2'),
    hasRPMStarboard: hasDevice('rpm-2'),
    hasFMCenter: hasDevice('fm-3'),
    hasRPMCenter: hasDevice('rpm-3'),
  };
}

export function convertAEKeyToReadableString(str: string) {
  if (/^totalAE/i.test(str)) {
    return str
      .replace(/^total/i, 'Total ')
      .replace(/([A-Z])/g, ' $1')
      .trim();
  }

  const match = str.match(/ae(\d+)(.*)/i);
  if (!match) return str;

  const [, number, rest] = match;

  return `AE ${number} ${rest.replace(/([A-Z])/g, ' $1').trim()}`;
}

export const convertShortAELabel = (key: string) => {
  if (/^totalAEFuelCons/i.test(key)) return 'Total AE Fuel Cons';
  if (/^totalAERunningTime/i.test(key)) return 'Total AE Running Time';

  const match = key.match(/ae(\d+)/i);
  return match ? `AE ${match[1]}` : key;
};

export const convertShortFMLabel = (key: string) => {
  if (/^totalEngineFuelCons/i.test(key)) return 'Total Engine Fuel Cons';
  const match = key.match(/^fm(.*?)TotalFuel$/i);
  return match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : key;
};

export const uniqueByLabel = (items: { key: string; label: string }[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.label)) return false;
    seen.add(item.label);
    return true;
  });
};
